import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  getTeamCategories, createTeamCategory, updateTeamCategory, deleteTeamCategory,
  getNewsCategories, createNewsCategory, updateNewsCategory, deleteNewsCategory
} from "@/lib/api";

const initialTeamData = { name: "", code: "", order: 0, is_active: true };
const initialNewsData = { name: "", code: "", order: 0, is_active: true };

export default function AdminCategories() {
  const [teamCategories, setTeamCategories] = useState([]);
  const [newsCategories, setNewsCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState("team");
  const [formData, setFormData] = useState(initialTeamData);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchCategories = async () => {
    try {
      const [teamRes, newsRes] = await Promise.all([
        getTeamCategories(),
        getNewsCategories()
      ]);
      setTeamCategories(teamRes.data);
      setNewsCategories(newsRes.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSwitchChange = (checked) => {
    setFormData({ ...formData, is_active: checked });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const data = { ...formData, order: parseInt(formData.order) };

    try {
      if (dialogType === "team") {
        if (editingId) {
          await updateTeamCategory(editingId, data);
          toast.success("Team category updated");
        } else {
          await createTeamCategory(data);
          toast.success("Team categorie aangemaakt");
        }
      } else {
        if (editingId) {
          await updateNewsCategory(editingId, data);
          toast.success("Nieuws categorie bijgewerkt");
        } else {
          await createNewsCategory(data);
          toast.success("Nieuws categorie aangemaakt");
        }
      }
      setDialogOpen(false);
      setFormData(initialTeamData);
      setEditingId(null);
      fetchCategories();
    } catch (error) {
      toast.error("Kon categorie niet opslaan");
      console.error("Fout bij het opslaan van categorie:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (category, type) => {
    setDialogType(type);
    setFormData({
      name: category.name,
      code: category.code,
      order: category.order.toString(),
      is_active: category.is_active,
    });
    setEditingId(category.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm("Weet je zeker dat je deze categorie wilt verwijderen?")) return;

    try {
      if (type === "team") {
        await deleteTeamCategory(id);
      } else {
        await deleteNewsCategory(id);
      }
      toast.success("Categorie verwijderd");
      fetchCategories();
    } catch (error) {
      toast.error("Kon categorie niet verwijderen");
      console.error("Fout bij het verwijderen van categorie:", error);
    }
  };

  const openNewDialog = (type) => {
    setDialogType(type);
    setFormData(type === "team" ? initialTeamData : initialNewsData);
    setEditingId(null);
    setDialogOpen(true);
  };

  return (
    <div data-testid="admin-categories-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">Categorieën</h1>
        <p className="text-slate-600">Beheer team- en nieuwscategorieën</p>
      </div>

      <Tabs defaultValue="team">
        <TabsList className="mb-6 bg-slate-200">
          <TabsTrigger value="team" className="data-[state=active]:bg-white data-[state=active]:text-slate-900">
            Team Categorieën
          </TabsTrigger>
          <TabsTrigger value="news" className="data-[state=active]:bg-white data-[state=active]:text-slate-900">
            Nieuws Categorieën
          </TabsTrigger>
        </TabsList>

        {/* Team Categories */}
        <TabsContent value="team">
          <Card className="border-slate-200 bg-white">
            <CardHeader className="flex flex-row items-center justify-between bg-white">
              <CardTitle className="text-slate-900">Team Categorieën</CardTitle>
              <Button onClick={() => openNewDialog("team")} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-team-category">
                <Plus className="h-4 w-4 mr-2" />
                Categorie Toevoegen
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-slate-700">Volgorde</TableHead>
                    <TableHead className="text-slate-700">Naam</TableHead>
                    <TableHead className="text-slate-700">Code</TableHead>
                    <TableHead className="text-slate-700">Status</TableHead>
                    <TableHead className="text-right text-slate-700">Acties</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-slate-600">Laden...</TableCell>
                    </TableRow>
                  ) : teamCategories.length > 0 ? (
                    teamCategories.map((cat) => (
                      <TableRow key={cat.id}>
                        <TableCell className="text-slate-700">{cat.order}</TableCell>
                        <TableCell className="font-medium text-slate-900">{cat.name}</TableCell>
                        <TableCell className="text-slate-700">{cat.code}</TableCell>
                        <TableCell>
                          <Badge className={cat.is_active ? "bg-green-500 text-white" : "bg-slate-400 text-white"}>
                            {cat.is_active ? "Actief" : "Inactief"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(cat, "team")} className="text-slate-600 hover:text-slate-900">
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(cat.id, "team")} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-slate-600">Geen team categorieën gevonden</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* News Categories */}
        <TabsContent value="news">
          <Card className="border-slate-200 bg-white">
            <CardHeader className="flex flex-row items-center justify-between bg-white">
              <CardTitle className="text-slate-900">Nieuws Categorieën</CardTitle>
              <Button onClick={() => openNewDialog("news")} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-news-category">
                <Plus className="h-4 w-4 mr-2" />
                Categorie Toevoegen
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-slate-700">Order</TableHead>
                    <TableHead className="text-slate-700">Name</TableHead>
                    <TableHead className="text-slate-700">Code</TableHead>
                    <TableHead className="text-slate-700">Status</TableHead>
                    <TableHead className="text-right text-slate-700">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-slate-600">Laden...</TableCell>
                    </TableRow>
                  ) : newsCategories.length > 0 ? (
                    newsCategories.map((cat) => (
                      <TableRow key={cat.id}>
                        <TableCell className="text-slate-700">{cat.order}</TableCell>
                        <TableCell className="font-medium text-slate-900">{cat.name}</TableCell>
                        <TableCell className="text-slate-700">{cat.code}</TableCell>
                        <TableCell>
                          <Badge className={cat.is_active ? "bg-green-500 text-white" : "bg-slate-400 text-white"}>
                            {cat.is_active ? "Actief" : "Inactief"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(cat, "news")} className="text-slate-600 hover:text-slate-900">
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(cat.id, "news")} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-slate-600">Geen nieuws categorieën gevonden</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900">
              {editingId ? "Bewerken" : "Toevoegen"} {dialogType === "team" ? "Team" : "Nieuws"} Categorie
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-700">Naam</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="border-slate-300"
                placeholder={dialogType === "team" ? "bijv. Eerste ploeg" : "bijv. Wedstrijdverslag"}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="code" className="text-slate-700">Code</Label>
              <Input
                id="code"
                name="code"
                value={formData.code}
                onChange={handleChange}
                required
                className="border-slate-300"
                placeholder={dialogType === "team" ? "bijv. EERSTE" : "bijv. wedstrijd"}
              />
              <p className="text-xs text-slate-500">Used for URLs and filtering</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="order" className="text-slate-700">Weergavevolgorde</Label>
              <Input
                id="order"
                name="order"
                type="number"
                min="0"
                value={formData.order}
                onChange={handleChange}
                className="border-slate-300"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="is_active" className="text-slate-700">Actief</Label>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={handleSwitchChange}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-300 text-slate-700">
                Annuleren
              </Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
                {saving ? "Opslaan..." : "Opslaan"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
